import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

const DEFAULT_ACTIONS = {
  available: false,
  onImport: null,
  onStartExport: null,
};

// A non-null default (registerActions as a safe no-op) so any consumer
// rendered without a provider — a test, a story, a future route — still
// works instead of throwing on `registerActions` being null.
const IntegrationsActionsContext = createContext({
  ...DEFAULT_ACTIONS,
  registerActions: () => {},
});

// Bridges the Integrations menu (rendered in the global TopNav, above the
// router) to whichever handlers the mounted IntegrationsPage registers. Only
// IntegrationsPage calls registerActions — every other route leaves
// `available: false`, so the menu falls back to Create Integration only.
export function IntegrationsActionsProvider({ children }) {
  const [actions, setActions] = useState(DEFAULT_ACTIONS);

  const registerActions = useCallback((next) => {
    setActions((prev) => ({ ...prev, ...next }));
  }, []);

  const value = useMemo(
    () => ({ ...actions, registerActions }),
    [actions, registerActions],
  );

  return (
    <IntegrationsActionsContext.Provider value={value}>
      {children}
    </IntegrationsActionsContext.Provider>
  );
}

IntegrationsActionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useIntegrationsActions = () =>
  useContext(IntegrationsActionsContext);
